import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

export type PublishedArticle = {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  body_markdown: string;
  hero_image_url: string | null;
  hero_image_alt: string | null;
  category: string;
  read_minutes: number;
  faqs: Array<{ q: string; a: string }>;
  meta_title: string | null;
  meta_description: string | null;
  chris_take: string | null;
  date_published: string | null;
  date_updated: string | null;
  published_at: string | null;
};

/** Fetch a single PUBLISHED article by slug (public). Returns null if not found. */
export const getPublishedArticleBySlug = createServerFn({ method: "GET" })
  .inputValidator((input: { slug: string }) =>
    z.object({ slug: z.string().min(1).max(200) }).parse(input)
  )
  .handler(async ({ data }): Promise<PublishedArticle | null> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const fullSlug = data.slug.startsWith("/learn/") ? data.slug : `/learn/${data.slug}`;
    const { data: row, error } = await supabaseAdmin
      .from("generated_articles")
      .select("id, slug, title, excerpt, body_markdown, hero_image_url, hero_image_alt, category, read_minutes, faqs, meta_title, meta_description, chris_take, date_published, date_updated, published_at")
      .eq("slug", fullSlug)
      .eq("status", "published")
      .maybeSingle();
    if (error) {
      console.error("getPublishedArticleBySlug error:", error.message);
      return null;
    }
    return row as PublishedArticle | null;
  });

/** List all published articles (for sitemap, RSS, /learn index). */
export const listPublishedArticles = createServerFn({ method: "GET" }).handler(
  async (): Promise<PublishedArticle[]> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin
      .from("generated_articles")
      .select("id, slug, title, excerpt, body_markdown, hero_image_url, hero_image_alt, category, read_minutes, faqs, meta_title, meta_description, chris_take, date_published, date_updated, published_at")
      .eq("status", "published")
      .order("published_at", { ascending: false });
    if (error) {
      console.error("listPublishedArticles error:", error.message);
      return [];
    }
    return (data ?? []) as PublishedArticle[];
  }
);
